// R1b-δ — Public verification proof lookup.
// Calls GET /api/verify/{proof_hash} which is a public endpoint.
import React, { useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Search } from "lucide-react";
import StatusChip from "../../../components/StatusChip";

export default function AuditProofSearchTab() {
  const [hash, setHash] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const search = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.get(`/verify/${encodeURIComponent(hash.trim())}`);
      setResult(r.data);
    } catch (e) {
      if (e.response?.status === 404) setError("No document found for this proof hash.");
      else setError(formatApiError(e.response?.data?.detail) || "Verification failed.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="audit-proof-search-body">
      <div className="mb-4">
        <div className="font-display font-semibold text-erf-dark text-lg">Verification Proof Search</div>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5">Paste a proof hash from a printed or exported document to verify authenticity.</div>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          value={hash}
          onChange={(e) => setHash(e.target.value)}
          placeholder="Paste sha256 proof hash…"
          className="font-mono h-10"
          data-testid="proof-search-input"
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
        />
        <Button onClick={search} disabled={busy || !hash.trim()} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover h-10" data-testid="proof-search-btn">
          <Search className="w-4 h-4 mr-1" /> {busy ? "Verifying…" : "Verify"}
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm font-nav" data-testid="proof-search-error">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-green-300 bg-green-50 text-green-900 px-5 py-4" data-testid="proof-search-result">
          <div className="flex items-center gap-2 mb-3">
            <div className="font-display font-semibold text-green-900">Verified</div>
            <StatusChip status="connected" label="Authentic" />
          </div>
          <div className="grid gap-2 md:grid-cols-2 text-[12px] font-mono">
            <div><span className="text-green-900/60">filename:</span> {result.filename || "—"}</div>
            <div><span className="text-green-900/60">classification:</span> {result.classification || result.class || "—"}</div>
            <div><span className="text-green-900/60">sha256:</span> {result.sha256 ? result.sha256.slice(0, 32) + "…" : "—"}</div>
            <div><span className="text-green-900/60">tenant:</span> {result.tenant_display_name || "Redacted"}</div>
            <div><span className="text-green-900/60">verified_at:</span> {result.verified_at ? new Date(result.verified_at).toLocaleString() : new Date().toLocaleString()}</div>
            <div><span className="text-green-900/60">contract_id:</span> {result.contract_id ? String(result.contract_id).slice(0, 12) : "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}
